import forexFutures from "../../../constants/forexFuturesConstants"; // Import forex constants

export const detectFVG = (
  currencyPair,
  data,
  gapThreshold,
  startDate = null,
  currentPrice = null // <-- Accept currentPrice as a param
) => {
  const bullishFVG = [];
  const bearishFVG = [];

  // Helper function to get the number of decimal places in gapThreshold
  const getDecimalPlaces = (num) => {
    const str = num.toString();
    if (str.includes(".")) {
      return str.split(".")[1].length;
    }
    return 0; // No decimal places
  };

  const decimalPlaces = getDecimalPlaces(gapThreshold);

  // Retrieve tick size and value for the given currency pair
  const forexData = forexFutures[currencyPair];
  const tickSize = forexData?.tickSize || null;
  const tickValue = forexData?.tickValue || null;

  if (!forexData) {
    console.warn(
      `Tick size or value is not defined for currency pair: ${currencyPair}`
    );
  }

  for (let i = 2; i < data.length; i++) {
    const firstCandle = data[i - 2];
    const secondCandle = data[i - 1];
    const thirdCandle = data[i];

    // If startDate is provided, skip entries before the startDate
    if (startDate && new Date(secondCandle.time) < new Date(startDate)) {
      continue;
    }

    // =============================
    //     BULLISH FVG CHECK
    // =============================
    // Condition: (low of the 1st candle) - (high of the 3rd) >= gapThreshold
    let bullishGap = firstCandle.low - thirdCandle.high;
    bullishGap = parseFloat(bullishGap.toFixed(decimalPlaces));

    // Potential gain in ticks from just the "gap" itself
    let bullishPotentialGain = "No value found";
    if (tickSize && tickValue) {
      const gapInTicks = Math.floor(bullishGap / tickSize);
      bullishPotentialGain = gapInTicks * tickValue; 
    }

    // Potential gain from current price: 
    // Example formula: if current price < firstCandle.low, 
    //   difference = firstCandle.low - currentPrice
    let bullishPotentialGainFromCurrent = "N/A";
    if (currentPrice !== null && tickSize && tickValue) {
      const diff = firstCandle.low - currentPrice; 
      // convert to ticks
      const diffInTicks = Math.floor(diff / tickSize); 
      const diffUSD = diffInTicks * tickValue;
      // you might want to clamp at 0 if negative, or keep negative if below
      bullishPotentialGainFromCurrent = parseFloat(diffUSD.toFixed(0));
    }

    if (bullishGap >= gapThreshold) {
      bullishFVG.push({
        time: secondCandle.time,
        upper: firstCandle.low,
        lower: thirdCandle.high,
        firstCandleTime: firstCandle.time,
        firstCandleIndex: i - 2,
        thirdCandleTime: thirdCandle.time,
        thirdCandleIndex: i,
        gap: bullishGap,
        // Old potential gain (from the gap, in USD)
        potentialGain: bullishPotentialGain,
        // New field: potential gain from current price
        potentialGainFromCurrent: bullishPotentialGainFromCurrent,
        tickSize: tickSize,
        tickValue: tickValue
      });
    }

    // =============================
    //     BEARISH FVG CHECK
    // =============================
    // Condition: (low of 3rd candle) - (high of 1st) >= gapThreshold
    let bearishGap = thirdCandle.low - firstCandle.high;
    bearishGap = parseFloat(bearishGap.toFixed(decimalPlaces));

    // Potential gain in ticks from the "gap"
    let bearishPotentialGain = "No value found";
    if (tickSize && tickValue) {
      const gapInTicks = Math.floor(bearishGap / tickSize);
      bearishPotentialGain = gapInTicks * tickValue;
    }

    // Potential gain from current price:
    // Example formula: if current price > firstCandle.high,
    //   difference = currentPrice - firstCandle.high
    let bearishPotentialGainFromCurrent = "N/A";
    if (currentPrice !== null && tickSize && tickValue) {
      const diff = currentPrice - firstCandle.high;
      const diffInTicks = Math.floor(diff / tickSize);
      const diffUSD = diffInTicks * tickValue;
      bearishPotentialGainFromCurrent = parseFloat(diffUSD.toFixed(2));
    }

    if (bearishGap >= gapThreshold) {
      bearishFVG.push({
        time: secondCandle.time,
        upper: thirdCandle.low,
        lower: firstCandle.high,
        firstCandleTime: firstCandle.time,
        firstCandleIndex: i - 2,
        thirdCandleTime: thirdCandle.time,
        thirdCandleIndex: i,
        gap: bearishGap,
        potentialGain: bearishPotentialGain,
        potentialGainFromCurrent: bearishPotentialGainFromCurrent,
        tickSize: tickSize,
        tickValue: tickValue
      });
    }
  }

  // Sort the gaps by time
  bullishFVG.sort((a, b) => new Date(a.time) - new Date(b.time));
  bearishFVG.sort((a, b) => new Date(a.time) - new Date(b.time));

  return {
    bullishFVG,
    bearishFVG,
    earliestDate: bullishFVG.length > 0 ? bullishFVG[0].time : null,
  };
};
